import * as React from 'react';
import { Button, DialogTitle, DialogContent, DialogActions,
  DialogProps, Typography, 
  Paper} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Query } from 'system/query/Query';
import { BuilderContext } from 'components/queries/QueryBuilderPanel';
import { QuerySegment } from 'system/query/QuerySegment';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import ResponsiveDialog from 'components/dialogs/ResponsiveDialog';

export interface QuerySegmentOptionsDialogProps extends Omit<DialogProps, ('onClose' | 'open')> {
  isOpen: boolean;

  // Required callbacks to handle the user's action.
  // onCancel should close the dialog with no changes,
  // onConfirm should close the dialog and apply the user's changes.
  onCancel: (event: object) => void;
  onConfirm: (event: MouseEvent) => void;

  // The query segment being edited
  querySegment: QuerySegment;

  // The parent query segment, if any
  parent?: QuerySegment | Query;

  // The original query, before any changes were made
  originalQuery: Query;
  updateQuery: (query?: Query) => void;

  // The title of the dialog
  title: string;

  // Optional descriptive text to display below the title.
  // Alternately, you can pass children to display custom JSX
  description?: string;

  // Optional custom confirmation button text
  confirmText?: string;

  // Optional custom cancellation button text
  cancelText?: string;

  // Optional flag to show a confirmation dialog when the user cancels
  showCancelConfirmation?: boolean;

  // Optional custom onKeyDown handler
  onKeyDown?: (event: React.KeyboardEvent) => void;

  maxWidth?: "sm" | "xs" | "md" | "lg" | "xl" | undefined;
}

export default function QuerySegmentOptionsDialog(props: QuerySegmentOptionsDialogProps) {
  const { confirmText, cancelText } = props;
  const { i18n } = useTranslation();

  const [cancelConfirmationIsOpen, setCancelConfirmationIsOpen] = React.useState(false);

  const canDelete = props.parent && !((props.parent as any)?.query) && props.updateQuery;

  const {draggingIsDisabled, setDraggingIsDisabled} = React.useContext(BuilderContext);

  React.useEffect(() => {
    if (draggingIsDisabled !== props.isOpen) {
      setDraggingIsDisabled(props.isOpen);
    }
  }, [props.isOpen]);

  const handleCancel = (event:any) => {
    if (props.showCancelConfirmation) {
      setCancelConfirmationIsOpen(true);
    } else {
      props.onCancel(event);
    }
  };

  const handleSubmit = (event:any) => {
    props.onConfirm(event);
  };

  const handleDelete = (event:any) => {
    const parent = props.parent as any;
    if (parent.values) {
      parent.values = parent.values.filter((value: any) => value !== props.querySegment);
    } else if (parent.left) {
      delete parent.left;
    } else if (parent.right) {
      delete parent.right;
    }
    props.updateQuery();
    props.onCancel(event);
  };

  return (
    <ResponsiveDialog
      open={props.isOpen}
      maxWidth={props.maxWidth || "sm"}
      disableClickaway={true}
      onClose={(event) => {
        setDraggingIsDisabled(false);
        handleCancel(event);
      }}
      onKeyDown={props.onKeyDown}
    >
      <DialogTitle sx={{ minWidth: "400px" }}>
        <Typography variant="h4">
          {props.title}
        </Typography>
        { props.description &&
          <Typography variant="body1">
            {props.description}
          </Typography>
        }
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: 'background.default', p: 1 }} >
        {props.children}
      </DialogContent>
      <DialogActions sx={{ padding: "10px 18px 10px 0" }} >
        { canDelete &&
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={(event) => {
              setDraggingIsDisabled(false);
              handleDelete(event);
            }} >
            { cancelText || i18n.t("delete") }
          </Button>
        }
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={(event) => {
            setDraggingIsDisabled(false);
            handleCancel(event)
          }}>
          { cancelText || i18n.t("cancel") }
        </Button>
        <Button
          variant="contained"
          color='success'
          size="small"
          onClick={(event) => {
            setDraggingIsDisabled(false);
            handleSubmit(event);
          }}>
          { confirmText ||  i18n.t("save") }
        </Button>
      </DialogActions>
      {cancelConfirmationIsOpen &&
        <ConfirmationDialog
          isOpen={cancelConfirmationIsOpen}
          title={i18n.t("query-form.unsaved-changes-title")}
          description={i18n.t("query-form.close-dialog-confirmation")}
          onClose={(confirmed:boolean, event) => {
            setCancelConfirmationIsOpen(false);
            if (confirmed) {
              props.onCancel(event);
            }
          }}
        />
      }
    </ResponsiveDialog>
  );
}
